import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Table } from '../../components/ui/Table';
import { TableActionButton, TableActions } from '../../components/ui';
import { Eye } from 'lucide-react';
import { Badge } from '../../components/ui/Badge/Badge';
import { Select } from '../../components/ui/Select/Select';
import { DateInput } from '../../components/ui/DateInput/DateInput';
import { Modal } from '../../components/ui/Modal/Modal';
import { usePageHeader } from '@/hooks/usePageHeader';
import { PageContainer } from '@/components/ui/PageContainer/PageContainer';
import { apiFetch } from '@/lib/api';
import { formatDateTimeStr } from '@/lib/localeFormatters';
import { t } from '@/lib/i18n';

interface LogRow {
  id: string;
  created_at: string;
  provider: string;
  model: string;
  purpose: string;
  endpoint: string;
  input_tokens: number;
  output_tokens: number;
  total_cost_usd: string;
  latency_ms: number;
  status: 'processing' | 'success' | 'error';
  error_code?: string;
  error_message?: string;
  organization_id: string | null;
  organization_name: string | null;
}

interface FilterOptions {
  providers: string[];
  models: string[];
  purposes: string[];
  statuses: string[];
  sharedOrgs: { id: string; name: string }[];
}

type Scope = 'platform+shared' | 'platform' | 'shared';

const SCOPE_OPTIONS = [
  { value: 'platform+shared', label: t('Platform + Shared') },
  { value: 'platform', label: t('Platform Only') },
  { value: 'shared', label: t('Shared Only') },
];

export function LLMLogs() {
  usePageHeader({ title: t('LLM Logs'), breadcrumbs: [{ label: t('Admin'), path: '/admin/orgs' }] });

  const [scope, setScope] = useState<Scope>('platform+shared');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [orgId, setOrgId] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(15);
  const [tableFilters, setTableFilters] = useState<Record<string, string>>({});
  const [detail, setDetail] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Fetch filter options
  const { data: filterOptions } = useQuery<FilterOptions>({
    queryKey: ['admin-llm-filter-options', scope],
    queryFn: async () => {
      const params = new URLSearchParams({ scope });
      const res = await apiFetch(`/api/admin/llm/filter-options?${params.toString()}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Failed to load filter options');
      return json.data;
    },
  });

  // Fetch logs
  const { data: logsResult, isLoading, error } = useQuery<{ items: LogRow[]; total: number }>({
    queryKey: ['admin-llm-logs', scope, dateFrom, dateTo, orgId, currentPage, currentPageSize, tableFilters],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(currentPage),
        pageSize: String(currentPageSize),
        scope,
      });
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      if (orgId) params.set('orgId', orgId);
      if (tableFilters.provider) params.set('provider', tableFilters.provider);
      if (tableFilters.model) params.set('model', tableFilters.model);
      if (tableFilters.status) params.set('status', tableFilters.status);
      if (tableFilters.purpose) params.set('purpose', tableFilters.purpose);
      const res = await apiFetch(`/api/admin/llm/logs?${params.toString()}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Failed to load logs');
      return { items: json.data || [], total: json.pagination?.total || 0 };
    },
  });

  const logs = logsResult?.items || [];
  const totalItems = logsResult?.total || 0;
  const errorMessage = error instanceof Error ? error.message : null;

  const handleFilter = (newFilters: Record<string, string>) => {
    setTableFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePaginate = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setCurrentPageSize(pageSize);
  };

  // Build dynamic filter options for the Table component
  const dynamicFilterOptions = useMemo(() => {
    const opts = [];
    opts.push({
      key: 'provider',
      label: t('Provider'),
      options: (filterOptions?.providers || []).map((p) => ({ label: p, value: p })),
    });
    opts.push({
      key: 'model',
      label: t('Model'),
      options: (filterOptions?.models || []).map((m) => ({ label: m, value: m })),
    });
    opts.push({
      key: 'purpose',
      label: t('Purpose'),
      options: (filterOptions?.purposes || []).map((p) => ({ label: p, value: p })),
    });
    opts.push({
      key: 'status',
      label: t('Status'),
      options: (filterOptions?.statuses || []).map((s) => ({ label: s.charAt(0).toUpperCase() + s.slice(1), value: s })),
    });
    return opts;
  }, [filterOptions]);

  const ALL = '_all';

  const orgOptions = useMemo(() => [
    { value: ALL, label: t('All Shared Orgs') },
    ...(filterOptions?.sharedOrgs || []).map((o) => ({ value: o.id, label: o.name })),
  ], [filterOptions?.sharedOrgs]);

  const clearable = (v: string) => v === ALL ? '' : v;

  const showOrgFilter = scope !== 'platform';

  const columns = [
    {
      key: 'created_at',
      title: t('Time'),
      render: (value: string) => formatDateTimeStr(value),
    },
    {
      key: 'organization_name',
      title: t('Organization'),
      render: (_: any, record: LogRow) => (
        record.organization_id ? (
          <Badge variant="secondary">{record.organization_name || 'Unknown Org'}</Badge>
        ) : (
          <Badge variant="outline">Platform</Badge>
        )
      ),
    },
    { key: 'provider', title: t('Provider') },
    { key: 'model', title: t('Model') },
    { key: 'purpose', title: t('Purpose') },
    { key: 'endpoint', title: t('Endpoint') },
    {
      key: 'status',
      title: t('Status'),
      render: (value: 'processing' | 'success' | 'error') => (
        <Badge variant={value === 'success' ? 'success' : value === 'error' ? 'error' : 'warning'}>
          {value}
        </Badge>
      ),
    },
    { key: 'latency_ms', title: t('Latency (ms)'), align: 'right' as const },
    {
      key: 'tokens',
      title: t('Tokens'),
      render: (_: any, record: LogRow) => (
        <span>
          {record.input_tokens} / {record.output_tokens}
        </span>
      ),
      align: 'right' as const,
    },
    {
      key: 'total_cost_usd',
      title: t('Cost (USD)'),
      render: (value: string) => `$${parseFloat(value || '0').toFixed(6)}`,
      align: 'right' as const,
    },
    {
      key: 'actions',
      title: t('Actions'),
      align: 'right' as const,
      render: (_: any, record: LogRow) => (
        <TableActions>
          <TableActionButton
            icon={Eye}
            onClick={async () => {
              setDetailLoading(true);
              try {
                const res = await apiFetch(`/api/admin/llm/logs/${record.id}`);
                const json = await res.json();
                if (json.success) setDetail(json.data);
              } finally {
                setDetailLoading(false);
              }
            }}
          >
            View
          </TableActionButton>
        </TableActions>
      )
    },
  ];

  return (
    <PageContainer>
      {/* Scope and date filters above the Table's built-in filters */}
      <div className="bg-card border border-border rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <Select
            label="Scope"
            options={SCOPE_OPTIONS}
            value={scope}
            onValueChange={(v) => {
              setScope(v as Scope);
              if (v === 'platform') setOrgId('');
              setCurrentPage(1);
            }}
          />
          <DateInput
            label="Date from"
            value={dateFrom}
            onChange={(v) => { setDateFrom(v); setCurrentPage(1); }}
          />
          <DateInput
            label="Date to"
            value={dateTo}
            onChange={(v) => { setDateTo(v); setCurrentPage(1); }}
          />
          {showOrgFilter && (
            <Select
              label="Organization"
              options={orgOptions}
              value={orgId || ALL}
              onValueChange={(v) => { setOrgId(clearable(v)); setCurrentPage(1); }}
              placeholder="All Shared Orgs"
            />
          )}
        </div>
      </div>

      {errorMessage && (
        <div className="mb-4 p-4 bg-error-50 border border-error-200 rounded-md">
          <div className="text-sm text-error-600">{errorMessage}</div>
        </div>
      )}

      <Table
        data={logs}
        columns={columns}
        loading={isLoading}
        searchable={false}
        filters={dynamicFilterOptions}
        pagination={{ pageSize: 15, showSizeChanger: true, pageSizeOptions: [10, 15, 25, 50] }}
        emptyText="No logs"
        serverSide={true}
        onFilter={handleFilter}
        onPaginate={handlePaginate}
        totalItems={totalItems}
      />

      {detail && (
        <Modal
          isOpen={!!detail}
          onClose={() => setDetail(null)}
          title="LLM Call Details"
          size="lg"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-sm font-medium text-muted-foreground mb-1">Provider / Model</div>
                <div className="text-sm text-foreground">{detail.provider} / {detail.model}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground mb-1">Purpose</div>
                <div className="text-sm text-foreground">{detail.purpose}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground mb-1">Organization</div>
                <div className="text-sm text-foreground">
                  {detail.organization_id ? (detail.organization_name || detail.organization_id) : 'Platform'}
                </div>
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-muted-foreground mb-1">Input</div>
              <div className="p-3 border rounded bg-muted whitespace-pre-wrap text-sm max-h-72 overflow-auto">
                {detail.request_text || '\u2014'}
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-muted-foreground mb-1">Output</div>
              <div className="p-3 border rounded bg-muted whitespace-pre-wrap text-sm max-h-72 overflow-auto">
                {detail.response_text || '\u2014'}
              </div>
            </div>

            {detail.status === 'error' && (detail.error_message || detail.error_code) && (
              <div className="bg-error-50 border border-error-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <h4 className="font-medium text-error-900 mb-1">Error Details</h4>
                    {detail.error_message && (
                      <p className="text-sm text-error-700 mb-2">{detail.error_message}</p>
                    )}
                    {detail.error_code && (
                      <p className="text-xs text-error-600">Code: {detail.error_code}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}
    </PageContainer>
  );
}
